import type { DashboardFilters } from "../types/dashboard";
import { monthKey } from "./monthlyData";

export interface ResolvedWidgetData {
  labels: string[];
  series: Array<{ label: string; values: number[] }>;
}

export interface EmissionQueryParams {
  businessUnitIds: string;
  from_date: Date;
  to_date: Date;
  categories: string;
  scopes?: string;
}

export function buildEmissionQueryParams(
  filters: DashboardFilters,
  businessUnitIds: string,
): EmissionQueryParams {
  return {
    businessUnitIds,
    from_date: filters.dateRange.from,
    to_date: filters.dateRange.to,
    categories: filters.categoryIds.join(","),
    scopes: filters.scope === "all" ? undefined : filters.scope,
  };
}

function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}
const empty = (): ResolvedWidgetData => ({ labels: [], series: [] });

/** A valid empty response resolves to empty data; null means a malformed contract. */
export function resolveWidgetData(
  source: string,
  raw: unknown,
): ResolvedWidgetData | null {
  if (
    source === "emissionsbyscopeandcategories" ||
    source === "allemissionsbymonth"
  ) {
    if (!record(raw) || !Array.isArray(raw.data) || !Array.isArray(raw.xaxis))
      return null;
    if (
      !raw.xaxis.every(
        (axis) =>
          record(axis) &&
          typeof axis.months === "string" &&
          monthKey(axis.months),
      )
    )
      return null;
    const labels = raw.xaxis.map((axis) => axis.months as string);
    const series: ResolvedWidgetData["series"] = [];
    for (const row of raw.data) {
      if (
        !record(row) ||
        typeof row.name !== "string" ||
        !Array.isArray(row.data)
      )
        return null;
      const values = new Map<string, number>();
      for (const point of row.data) {
        if (!record(point) || !monthKey(point.name) || !finite(point.value))
          return null;
        const key = monthKey(point.name);
        if (!labels.some((label) => monthKey(label) === key)) return null;
        values.set(key, (values.get(key) ?? 0) + point.value);
      }
      series.push({
        label: row.name,
        values: labels.map((label) => values.get(monthKey(label)) ?? 0),
      });
    }
    return series.length ? { labels, series } : empty();
  }
  if (!Array.isArray(raw)) return null;
  if (raw.length === 0) return empty();
  if (source === "emissionsbyunit" || source === "emissionsbyactivity") {
    if (
      !raw.every(
        (row) =>
          record(row) && typeof row.name === "string" && finite(row.value),
      )
    )
      return null;
    const sorted = [...raw].sort((a, b) => b.value - a.value);
    return {
      labels: sorted.map((row) => row.name),
      series: [{ label: "Emissions", values: sorted.map((row) => row.value) }],
    };
  }
  if (source === "emissionsbyyear") {
    if (
      !raw.every((row) => record(row) && finite(row.year) && finite(row.value))
    )
      return null;
    const sorted = [...raw].sort((a, b) => a.year - b.year);
    return {
      labels: sorted.map((row) => String(row.year)),
      series: [{ label: "Emissions", values: sorted.map((row) => row.value) }],
    };
  }
  if (source === "allemissions") {
    const values = new Map<string, number>();
    for (const scope of raw) {
      if (
        !record(scope) ||
        typeof scope.code !== "string" ||
        !finite(scope.value) ||
        !Array.isArray(scope.categories)
      )
        return null;
      for (const category of scope.categories) {
        if (
          !record(category) ||
          typeof category.code !== "string" ||
          typeof category.name !== "string" ||
          !finite(category.value) ||
          !Array.isArray(category.subcategories)
        )
          return null;
        if (
          !category.subcategories.every(
            (sub) =>
              record(sub) && typeof sub.code === "string" && finite(sub.value),
          )
        )
          return null;
        values.set(
          category.name,
          (values.get(category.name) ?? 0) + category.value,
        );
      }
    }
    if (!values.size) return empty();
    const entries = [...values.entries()].sort((a, b) => b[1] - a[1]);
    return {
      labels: entries.map(([label]) => label),
      series: [
        { label: "Emissions", values: entries.map(([, value]) => value) },
      ],
    };
  }
  return null;
}
